import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  Github,
  Linkedin,
  ArrowRight,
  Award,
  Code,
  Server,
  Cpu,
  Boxes,
  CodepenIcon,
  Brain,
  Zap,
  Twitter,
  BookOpen,
} from "lucide-react";

import ProjectCard from "../../components/ui/ProjectCard";
import ContactForm from "../../components/forms/ContactForm";
import { useProfile } from "../../contexts/ProfileContext";
import { usePublications } from "../../contexts/PublicationContext";
import { usePubPackages } from "../../contexts/PubPackageContext";
import { useStarterTemplates } from "../../contexts/StarterTemplateContext";
import { useBlogs } from "../../contexts/BlogContext";
import { Project } from "../../firebase/projectService";
import { useQuery } from "@tanstack/react-query";
import { featuredProjectsQuery } from "../../queries/contentQueries";
import { HomePageSkeleton, ProjectGridSkeleton } from "../../components/skeletons/PageSkeletons";
import { usePageTitle } from "../../hooks/usePageTitle";
import { isYouTubeUrl, getYouTubeVideoId, getMediaType, getYouTubeEmbedUrl } from "../../utils/mediaUtils";
import {
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  BeakerIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CodeBracketIcon,
  WrenchScrewdriverIcon,
  DocumentTextIcon,
  HeartIcon,
  ArrowDownTrayIcon,
  PlayIcon,
  ClockIcon,
  StarIcon as StarOutlineIcon,
  TagIcon,
  EyeIcon,
  CalendarIcon,
  CheckBadgeIcon,
  PhotoIcon
} from "@heroicons/react/24/outline";
import { StarIcon as StarSolidIcon } from "@heroicons/react/24/solid";



const HomePage: React.FC = () => {
  usePageTitle("Home");
  
  const { profile, loading } = useProfile();
  const { publications } = usePublications();
  const { featuredPubPackages } = usePubPackages();
  const { featuredStarterTemplates } = useStarterTemplates();
  const { featuredBlogs } = useBlogs();

  // Animation controls
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  // Featured Projects
  const {
    data: featuredProjects = [],
    isPending: projectsLoading,
    isError: projectsFailed,
  } = useQuery(featuredProjectsQuery);
  const projectsError = projectsFailed ? "Failed to load featured projects" : null;
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);



  // State for collapsible skills sections - initialize as collapsed
  const [collapsedSkills, setCollapsedSkills] = useState<{[key: string]: boolean}>({});
  const [skillsInitialized, setSkillsInitialized] = useState(false);

  // Initialize collapsed state when profile loads
  useEffect(() => {
    if (profile?.skills && !skillsInitialized) {
      const initialCollapsedState: {[key: string]: boolean} = {};
      profile.skills.forEach((skillCategory: any) => {
        initialCollapsedState[skillCategory.id] = true;
      });
      setCollapsedSkills(initialCollapsedState);
      setSkillsInitialized(true);
    }
  }, [profile?.skills, skillsInitialized]);

  // Function to toggle skill category collapse state
  const toggleSkillCategory = (categoryId: string) => {
    setCollapsedSkills(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };



  if (loading) {
    return <HomePageSkeleton />;
  }

  // Default description for projects if not provided in profile
  const projectsDescription =
    profile.projectsDescription ||
    "Here are some of my recent projects. I've worked on a variety of apps and solutions to solve real-world problems.";

  return (
    <div>
      {/* Enhanced Hero Section with Gradient Background */}
      <section className="py-24 lg:pt-[var(--page-top)] bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-primary-900/30">
        <div className="container">
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            {/* Content Column */}
            <motion.div
              className="lg:col-span-7 lg:pr-8"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="flex items-center mb-4">
                <div className="h-1 w-12 bg-primary-500 mr-4"></div>
                <span className="text-primary-600 dark:text-primary-400 font-medium tracking-wide">
                  Welcome
                </span>
              </div>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
                I'm{" "}
                <span className="text-primary-600 dark:text-primary-400 inline-block relative">
                  {profile.fullName}
                  <span className="absolute bottom-1 left-0 w-full h-3 bg-primary-200 dark:bg-primary-800/40 -z-10"></span>
                </span>
              </h1>

              <h2 className="text-2xl md:text-3xl text-gray-700 dark:text-gray-300 mb-6 font-light">
                {profile.title}
              </h2>

              <p className="text-lg text-justify text-gray-600 dark:text-gray-400 mb-8 max-w-2xl leading-relaxed">
                {profile.shortBio || profile.bio}
              </p>

              {/* Action buttons */}
              <div className="flex flex-wrap gap-4 mb-8">
                <Link to="/contact" className="btn-primary flex items-center">
                  Get in Touch
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
                <Link to="/projects" className="btn-outline flex items-center">
                  View Projects
                  <Code className="w-4 h-4 ml-2" />
                </Link>
              </div>

              {/* Social & Tech Showcase - Reorganized for better visual appeal */}
              <div className="space-y-6">
                {/* Social Media Links - More prominent positioning */}
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400 mr-2">
                    Connect:
                  </span>
                  <a
                    href={profile.socialLinks?.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 p-2.5 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800/50 transition-colors"
                    aria-label="LinkedIn Profile"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                  <a
                    href={profile.socialLinks?.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 p-2.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    aria-label="GitHub Profile"
                  >
                    <Github className="w-5 h-5" />
                  </a>
                  {profile.socialLinks?.twitter && (
                    <a
                      href={profile.socialLinks.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-sky-100 dark:bg-sky-900/30 text-sky-500 dark:text-sky-400 p-2.5 rounded-full hover:bg-sky-200 dark:hover:bg-sky-800/50 transition-colors"
                      aria-label="Twitter Profile"
                    >
                      <Twitter className="w-5 h-5" />
                    </a>
                  )}
                  {profile.socialLinks?.googleScholar && (
                    <a
                      href={profile.socialLinks.googleScholar}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 p-2.5 rounded-full hover:bg-orange-200 dark:hover:bg-orange-800/50 transition-colors"
                      aria-label="Google Scholar Profile"
                    >
                      <BookOpen className="w-5 h-5" />
                    </a>
                  )}
                </div>

                {/* Technology Specializations - Organized in a grid */}
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
                    Specializations:
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    <div className="flex items-center bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-900/10 px-3 py-2 rounded-lg border border-primary-200 dark:border-primary-800">
                      <Code className="w-4 h-4 mr-2 text-primary-500" />
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                        Flutter
                      </span>
                    </div>

                    <div className="flex items-center bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-900/10 px-3 py-2 rounded-lg border border-blue-200 dark:border-blue-800">
                      <Server className="w-4 h-4 mr-2 text-blue-500" />
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                        FastAPI
                      </span>
                    </div>

                    <div className="flex items-center bg-gradient-to-r from-violet-50 to-violet-100 dark:from-violet-900/20 dark:to-violet-900/10 px-3 py-2 rounded-lg border border-violet-200 dark:border-violet-800">
                      <Cpu className="w-4 h-4 mr-2 text-violet-500" />
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                        Generative AI
                      </span>
                    </div>

                    <div className="flex items-center bg-gradient-to-r from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-900/10 px-3 py-2 rounded-lg border border-amber-200 dark:border-amber-800">
                      <Boxes className="w-4 h-4 mr-2 text-amber-500" />
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                        Blockchain
                      </span>
                    </div>

                    <div className="flex items-center bg-gradient-to-r from-cyan-50 to-cyan-100 dark:from-cyan-900/20 dark:to-cyan-900/10 px-3 py-2 rounded-lg border border-cyan-200 dark:border-cyan-800">
                      <CodepenIcon className="w-4 h-4 mr-2 text-cyan-500" />
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                        ReactJS
                      </span>
                    </div>
                    <div className="flex items-center bg-gradient-to-r from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-900/10 px-3 py-2 rounded-lg border border-indigo-200 dark:border-indigo-800">
                      <CodepenIcon className="w-4 h-4 mr-2 text-indigo-500" />
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                        Vibe Coding
                      </span>
                    </div>

                    <div className="flex items-center bg-gradient-to-r from-pink-50 to-pink-100 dark:from-pink-900/20 dark:to-pink-900/10 px-3 py-2 rounded-lg border border-pink-200 dark:border-pink-800">
                      <Brain className="w-4 h-4 mr-2 text-pink-500" />
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                        Psychology
                      </span>
                    </div>

                    <div className="flex items-center bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-900/10 px-3 py-2 rounded-lg border border-green-200 dark:border-green-800">
                      <Zap className="w-4 h-4 mr-2 text-green-500" />
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                        Neuro-Science
                      </span>
                    </div>

                    
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Image Column */}
            <motion.div
              className="lg:col-span-5 flex justify-center"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="relative">
                {/* Profile image with layered design */}
                <div className="absolute inset-0 bg-primary-200 dark:bg-primary-900/40 rounded-full transform translate-x-4 translate-y-4"></div>
                <div className="relative z-10 w-64 h-64 md:w-80 md:h-80 rounded-full overflow-hidden border-4 border-white dark:border-gray-800 shadow-xl">
                  <img
                    src={profile.photoURL}
                    alt={profile.fullName}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Experience tag */}
                <div className="absolute -bottom-4 -right-4 bg-white dark:bg-gray-800 px-5 py-3 rounded-lg shadow-md z-20">
                  <span className="text-primary-600 dark:text-primary-400 font-medium text-sm tracking-wide">
                    {profile.title}
                  </span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Featured Projects - Moving this section up for better visibility */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="container">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <div className="flex items-center mb-4">
                <div className="h-1 w-12 bg-primary-500 mr-4"></div>
                <span className="text-primary-600 dark:text-primary-400 font-medium tracking-wide uppercase text-sm">
                  Projects
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
                Featured Projects
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mt-4 max-w-2xl">
                {projectsDescription}
              </p>
            </div>
            <Link
              to="/projects"
              className="inline-flex items-center text-primary-600 dark:text-primary-400 mt-4 md:mt-0 group"
            >
              <span>View all projects</span>
              <ArrowRight className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {projectsLoading ? (
            <ProjectGridSkeleton count={3} />
          ) : projectsError ? (
            <div className="text-center py-8">
              <p className="text-red-500">{projectsError}</p>
            </div>
          ) : featuredProjects.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400">
                No projects available yet.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProjects.map((project) => (
                <div
                  key={project.id}
                  onClick={() => setSelectedProject(project)}
                  className="cursor-pointer"
                >
                  <ProjectCard
                    title={project.title}
                    description={project.description}
                    imageUrl={project.image}
                    tags={project.tags}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Skills Section - Enhanced with collapsible design like Resume page */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="container">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div className="max-w-4xl">
              <div className="flex items-center mb-4">
                <div className="h-1 w-12 bg-primary-500 mr-4"></div>
                <span className="text-primary-600 dark:text-primary-400 font-medium tracking-wide uppercase text-sm">
                  Expertise
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Technical Skills
              </h2>
              
              {/* Enhanced description with better formatting */}
              <div className="bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 border border-gray-200 dark:border-gray-600 shadow-sm">
                <div className="prose prose-lg dark:prose-invert max-w-none">
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4 text-justify">
                    {profile.skillsDescription ? (
                      profile.skillsDescription
                    ) : (
                      <>
                        <span className="font-semibold text-primary-600 dark:text-primary-400">Results-driven Flutter developer</span> with{" "}
                        <span className="bg-primary-100 dark:bg-primary-900/30 px-2 py-1 rounded-md font-medium text-primary-700 dark:text-primary-300">4+ years of experience</span>{" "}
                        crafting high-performance, user-centric mobile applications.
                      </>
                    )}
                  </p>
                  
                  {!profile.skillsDescription && (
                    <>
                      <div className="grid md:grid-cols-2 gap-4 mt-4">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 w-2 h-2 bg-primary-500 rounded-full mt-2"></div>
                          <div>
                            <span className="font-medium text-gray-800 dark:text-gray-200">Mobile Excellence:</span>
                            <span className="text-gray-600 dark:text-gray-400 ml-1 text-justify">
                              Specializing in Flutter (3+ years) and native Android (1+ year)
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 w-2 h-2 bg-secondary-500 rounded-full mt-2"></div>
                          <div>
                            <span className="font-medium text-gray-800 dark:text-gray-200">Core Strengths:</span>
                            <span className="text-gray-600 dark:text-gray-400 ml-1 text-justify">
                              State management, RESTful APIs, Firebase, Clean Architecture
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 w-2 h-2 bg-violet-500 rounded-full mt-2"></div>
                          <div>
                            <span className="font-medium text-gray-800 dark:text-gray-200">Beyond Mobile:</span>
                            <span className="text-gray-600 dark:text-gray-400 ml-1 text-justify">
                              FastAPI, ReactJS, Generative AI with Langchain/Langgraph
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 w-2 h-2 bg-amber-500 rounded-full mt-2"></div>
                          <div>
                            <span className="font-medium text-gray-800 dark:text-gray-200">Innovation Focus:</span>
                            <span className="text-gray-600 dark:text-gray-400 ml-1 text-justify">
                              Blockchain technologies creating seamless experiences
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-6 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg border-l-4 border-primary-500">
                        <p className="text-primary-700 dark:text-primary-300 font-medium italic text-justify">
                          "Creating seamless, innovative experiences across platforms through cutting-edge technology and user-centered design."
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <Link
              to="/resume"
              className="inline-flex items-center text-primary-600 dark:text-primary-400 mt-6 md:mt-0 group"
            >
              <span>View full resume</span>
              <ArrowRight className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <motion.div
            ref={ref}
            variants={containerVariants}
            initial="hidden"
            animate={controls}
            className="space-y-6"
          >
            {profile.skills.map((skillCategory: any, categoryIndex: number) => {
              const isCollapsed = collapsedSkills[skillCategory.id] !== false;
              return (
                <motion.div 
                  key={skillCategory.id} 
                  variants={itemVariants}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden"
                >
                  {/* Category Header */}
                  <div 
                    className="bg-gradient-to-r from-primary-600 to-primary-700 dark:from-primary-700 dark:to-primary-800 p-4 cursor-pointer hover:from-primary-700 hover:to-primary-800 dark:hover:from-primary-800 dark:hover:to-primary-900 transition-all duration-200"
                    onClick={() => toggleSkillCategory(skillCategory.id)}
                  >
                    <h3 className="text-xl font-bold text-white flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center mr-3">
                          <span className="text-white font-bold text-sm">
                            {skillCategory.category.charAt(0)}
                          </span>
                        </div>
                        {skillCategory.category}
                      </div>
                      <div className="flex items-center">
                        <span className="text-sm font-normal mr-2 opacity-80">
                          {isCollapsed ? 'Show' : 'Hide'}
                        </span>
                        {isCollapsed ? (
                          <ChevronDownIcon className="h-5 w-5 transition-transform duration-200" />
                        ) : (
                          <ChevronUpIcon className="h-5 w-5 transition-transform duration-200" />
                        )}
                      </div>
                    </h3>
                  </div>

                  <motion.div 
                    className="overflow-hidden"
                    initial={false}
                    animate={{ 
                      height: isCollapsed ? 0 : 'auto',
                      opacity: isCollapsed ? 0 : 1 
                    }}
                    transition={{ 
                      duration: 0.3, 
                      ease: "easeInOut",
                      opacity: { duration: 0.2 }
                    }}
                  >
                    <div className="p-6">
                      {skillCategory.subcategories ? (
                        // Display the new hierarchical structure
                        <div className="space-y-6">
                          {skillCategory.subcategories.map((subcategory: any, subIndex: number) => (
                            <div key={subIndex} className="border-l-4 border-primary-200 dark:border-primary-800 pl-4">
                              <h4 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-200 flex items-center">
                                <div className="w-2 h-2 bg-primary-500 rounded-full mr-2"></div>
                                {subcategory.name}
                              </h4>
                              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                                {subcategory.skills.map((skill: string, skillIndex: number) => (
                                  <div
                                    key={skillIndex}
                                    className="group relative bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 
                                             border border-gray-200 dark:border-gray-600 rounded-lg p-3 text-center 
                                             hover:shadow-md hover:border-primary-300 dark:hover:border-primary-600 
                                             transition-all duration-200 cursor-default"
                                  >
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-primary-700 dark:group-hover:text-primary-400 transition-colors">
                                      {skill}
                                    </span>
                                    {/* Subtle accent line */}
                                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-400 to-primary-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        // Backward compatibility for the old structure
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                          {skillCategory.items?.map((skill: string, index: number) => (
                            <div
                              key={index}
                              className="group relative bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 
                                       border border-gray-200 dark:border-gray-600 rounded-lg p-3 text-center 
                                       hover:shadow-md hover:border-primary-300 dark:hover:border-primary-600 
                                       transition-all duration-200 cursor-default"
                            >
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-primary-700 dark:group-hover:text-primary-400 transition-colors">
                                {skill}
                              </span>
                              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-400 to-primary-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Professional Profile Section - Combining Achievements and Certifications */}
      {(profile.softSkills?.length > 0 ||
        profile.certifications?.length > 0) && (
        <section className="py-20 bg-white dark:bg-gray-900">
          <div className="container">
            <div className="flex items-center mb-4">
              <div className="h-1 w-12 bg-primary-500 mr-4"></div>
              <span className="text-primary-600 dark:text-primary-400 font-medium tracking-wide uppercase text-sm">
                Professional
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-12">
              Professional Profile
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Soft Skills */}
              {profile.softSkills && profile.softSkills.length > 0 && (
                <div>
                  <h3 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white">
                    Key Competencies
                  </h3>
                  <div className="grid gap-6">
                    {profile.softSkills.slice(0, 3).map((skill) => (
                      <motion.div
                        key={skill.id}
                        className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border-l-4 border-primary-500"
                        whileHover={{ y: -5, transition: { duration: 0.2 } }}
                      >
                        <h4 className="font-semibold text-primary-600 dark:text-primary-400 mb-2 text-lg">
                          {skill.title}
                        </h4>
                        <p className="text-gray-600 dark:text-gray-400">
                          {skill.description}
                        </p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* Certifications Showcase */}
              {profile.certifications && profile.certifications.length > 0 && (
                <div>
                  <h3 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white">
                    Certifications
                  </h3>
                  <div className="grid gap-6">
                    {profile.certifications.slice(0, 3).map((cert) => (
                      <motion.div
                        key={cert.id}
                        className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border-l-4 border-secondary-500"
                        whileHover={{ y: -5, transition: { duration: 0.2 } }}
                      >
                        <div className="flex items-center mb-3">
                          <Award className="h-5 w-5 text-secondary-500 mr-2" />
                          <h4 className="font-semibold">{cert.name}</h4>
                        </div>
                        <p className="text-gray-600 dark:text-gray-400 text-sm mb-1">
                          {cert.issuer}
                        </p>
                        <p className="text-gray-500 dark:text-gray-500 text-sm mb-3">
                          {cert.date}
                        </p>
                        {cert.url && (
                          <a
                            href={cert.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-secondary-600 dark:text-secondary-400 text-sm inline-flex items-center hover:underline"
                          >
                            View Certificate
                            <ArrowRight className="ml-1 h-3 w-3" />
                          </a>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-12 text-center">
              <Link to="/resume" className="btn-outline">
                View Full Professional Profile
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Open Source Contributions Section */}
      <section className="py-20 bg-gradient-to-br from-indigo-50 via-blue-50 to-cyan-50 dark:from-gray-900 dark:via-indigo-900 dark:to-blue-900">
        <div className="container">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center mb-4">
              <div className="h-1 w-12 bg-primary-500 mr-4"></div>
              <span className="text-primary-600 dark:text-primary-400 font-medium tracking-wide uppercase text-sm">
                Open Source
              </span>
              <div className="h-1 w-12 bg-primary-500 ml-4"></div>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              Open Source Contributions
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Empowering the developer community with packages, tools, and comprehensive tutorials 
              that accelerate development and foster innovation.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Pub Packages */}
            <motion.div
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300"
              whileHover={{ y: -10, scale: 1.02 }}
            >
              <div className="flex items-center mb-6">
                <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-4 rounded-xl">
                  <CodeBracketIcon className="h-8 w-8 text-white" />
                </div>
                <div className="ml-4">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Flutter Packages
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Published on pub.dev
                  </p>
                </div>
              </div>
              
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                High-quality Flutter packages that solve real-world development challenges and speed up app development.
              </p>

              {featuredPubPackages.slice(0, 2).map((pkg) => (
                <div key={pkg.id} className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-900 dark:text-white">
                      {pkg.name}
                    </h4>
                    <StarSolidIcon className="h-5 w-5 text-yellow-400" />
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                    {pkg.description}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <div className="flex items-center space-x-3">
                      <span className="flex items-center">
                        <ArrowDownTrayIcon className="h-3 w-3 mr-1" />
                        {pkg.downloads ? (pkg.downloads >= 1000 ? `${(pkg.downloads/1000).toFixed(1)}K` : pkg.downloads) : 0}
                      </span>
                      <span className="flex items-center">
                        <HeartIcon className="h-3 w-3 mr-1" />
                        {pkg.likes || 0}
                      </span>
                    </div>
                    <span className="text-blue-600 dark:text-blue-400">
                      v{pkg.version}
                    </span>
                  </div>
                </div>
              ))}

              <div className="mt-6">
                <Link 
                  to="/pub-packages" 
                  className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
                >
                  View All Packages
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </div>
            </motion.div>

            {/* Development Tools & Templates */}
            <motion.div
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300"
              whileHover={{ y: -10, scale: 1.02 }}
            >
              <div className="flex items-center mb-6">
                <div className="bg-gradient-to-br from-purple-500 to-pink-600 p-4 rounded-xl">
                  <WrenchScrewdriverIcon className="h-8 w-8 text-white" />
                </div>
                <div className="ml-4">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Dev Tools & Templates
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Starter templates & tools
                  </p>
                </div>
              </div>
              
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Ready-to-use templates, extensions, and development tools to accelerate your development workflow.
              </p>

              {featuredStarterTemplates.slice(0, 2).map((template) => (
                <div key={template.id} className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      {template.icon ? (
                        <img
                          src={template.icon}
                          alt={template.title}
                          className="w-8 h-8 rounded object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                          <WrenchScrewdriverIcon className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                        </div>
                      )}
                      <h4 className="font-semibold text-gray-900 dark:text-white">
                        {template.title}
                      </h4>
                    </div>
                    <span className="text-xs px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded-full">
                      {template.type}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                    {template.description}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <div className="flex items-center space-x-3">
                      <span className="flex items-center">
                        <ArrowDownTrayIcon className="h-3 w-3 mr-1" />
                        {template.downloads ? (template.downloads >= 1000 ? `${(template.downloads/1000).toFixed(1)}K` : template.downloads) : 0}
                      </span>
                      <span className="flex items-center">
                        <StarOutlineIcon className="h-3 w-3 mr-1" />
                        {template.stars || 0}
                      </span>
                    </div>
                    <span className="text-purple-600 dark:text-purple-400">
                      {template.category}
                    </span>
                  </div>
                </div>
              ))}

              <div className="mt-6">
                <Link 
                  to="/starter-templates" 
                  className="inline-flex items-center text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 font-medium"
                >
                  Explore Tools & Templates
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </div>
            </motion.div>

            {/* Coding Tutorials */}
            <motion.div
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300"
              whileHover={{ y: -10, scale: 1.02 }}
            >
              <div className="flex items-center mb-6">
                <div className="bg-gradient-to-br from-green-500 to-blue-600 p-4 rounded-xl">
                  <DocumentTextIcon className="h-8 w-8 text-white" />
                </div>
                <div className="ml-4">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Coding  Tutorials & Tech Articles
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Bengali & English
                  </p>
                </div>
              </div>
              
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Comprehensive coding tutorials with YouTube videos, covering everything from basics to advanced concepts.
              </p>

              {featuredBlogs.slice(0, 2).map((blog) => (
                <div key={blog.id} className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-900 dark:text-white line-clamp-1">
                      {blog.title}
                    </h4>
                    <div className="flex items-center space-x-1">
                      {blog.youtubeVideoId && (
                        <PlayIcon className="h-4 w-4 text-red-600" />
                      )}
                      {blog.featured && (
                        <StarSolidIcon className="h-4 w-4 text-yellow-400" />
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2 line-clamp-2">
                    {blog.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <div className="flex items-center space-x-3">
                      <span className="flex items-center">
                        <ClockIcon className="h-3 w-3 mr-1" />
                        {blog.readingTime} min
                      </span>
                      <span className="flex items-center">
                        <HeartIcon className="h-3 w-3 mr-1" />
                        {blog.likes || 0}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        blog.difficulty === 'Beginner' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        blog.difficulty === 'Intermediate' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                        'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {blog.difficulty}
                      </span>
                      <span className="text-indigo-600 dark:text-indigo-400">
                        {blog.language}
                      </span>
                    </div>
                  </div>
                </div>
              ))}

              <div className="mt-6">
                <Link 
                  to="/blogs" 
                  className="inline-flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium"
                >
                  Read All Tutorials
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </div>
            </motion.div>
          </div>

          {/* Call to Action */}
          <div className="mt-16 text-center">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                🚀 Let's Build Something Amazing Together!
              </h3>
              <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
                Explore my open source contributions and join the community of developers 
                building the future of software development.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/pub-packages"
                  className="bg-white text-blue-600 hover:bg-gray-100 px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  Flutter Packages
                </Link>
                <Link
                  to="/starter-templates"
                  className="bg-white/20 hover:bg-white/30 text-white border border-white/30 px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  Dev Tools
                </Link>
                <Link
                  to="/blogs"
                  className="bg-white/20 hover:bg-white/30 text-white border border-white/30 px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  Tutorials
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Research Interests Section - For Academic Audiences */}
      {profile.researchInterests && profile.researchInterests.length > 0 && (
        <section className="py-20 bg-gray-50 dark:bg-gray-800">
          <div className="container">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
              <div className="max-w-4xl">
                <div className="flex items-center mb-4">
                  <div className="h-1 w-12 bg-primary-500 mr-4"></div>
                  <span className="text-primary-600 dark:text-primary-400 font-medium tracking-wide uppercase text-sm">
                    Research
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                  Research Focus
                </h2>
                
                {/* Enhanced description with better formatting */}
                <div className="bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 border border-gray-200 dark:border-gray-600 shadow-sm">
                  <div className="prose prose-lg dark:prose-invert max-w-none">
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4 text-justify">
                      {profile.researchSummary ? (
                        profile.researchSummary
                      ) : (
                        <>
                          <span className="font-semibold text-primary-600 dark:text-primary-400">Innovative researcher</span> focused on{" "}
                          <span className="bg-primary-100 dark:bg-primary-900/30 px-2 py-1 rounded-md font-medium text-primary-700 dark:text-primary-300">cutting-edge techniques</span>{" "}
                          to solve real-world problems through interdisciplinary approaches.
                        </>
                      )}
                    </p>
                    
                    {!profile.researchSummary && (
                      <>
                        <div className="grid md:grid-cols-2 gap-4 mt-4">
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0 w-2 h-2 bg-primary-500 rounded-full mt-2"></div>
                            <div>
                              <span className="font-medium text-gray-800 dark:text-gray-200">Research Areas:</span>
                              <span className="text-gray-600 dark:text-gray-400 ml-1 text-justify">
                                Technology integration in psychology and neuroscience
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0 w-2 h-2 bg-secondary-500 rounded-full mt-2"></div>
                            <div>
                              <span className="font-medium text-gray-800 dark:text-gray-200">Methodology:</span>
                              <span className="text-gray-600 dark:text-gray-400 ml-1 text-justify">
                                Evidence-based approaches with practical applications
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0 w-2 h-2 bg-violet-500 rounded-full mt-2"></div>
                            <div>
                              <span className="font-medium text-gray-800 dark:text-gray-200">Innovation:</span>
                              <span className="text-gray-600 dark:text-gray-400 ml-1 text-justify">
                                Bridging the gap between theoretical research and real-world solutions
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0 w-2 h-2 bg-amber-500 rounded-full mt-2"></div>
                            <div>
                              <span className="font-medium text-gray-800 dark:text-gray-200">Impact:</span>
                              <span className="text-gray-600 dark:text-gray-400 ml-1 text-justify">
                                Contributing to academic knowledge and industry advancement
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="mt-6 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg border-l-4 border-primary-500">
                          <p className="text-primary-700 dark:text-primary-300 font-medium italic text-justify">
                            "Advancing scientific understanding through rigorous research and innovative methodologies."
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
              {publications && publications.length > 0 && (
                <Link
                  to="/publications"
                  className="inline-flex items-center text-primary-600 dark:text-primary-400 mt-6 md:mt-0 group"
                >
                  <span>View publications</span>
                  <ArrowRight className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" />
                </Link>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {profile.researchInterests.slice(0, 3).map((interest, index) => (
                <motion.div
                  key={interest.id}
                  className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                  whileHover={{ y: -8, transition: { duration: 0.3 } }}
                >
                  <div className="flex items-center mb-4">
                    <div className="p-2 rounded-full bg-primary-100 dark:bg-primary-900/30 mr-4">
                      <BeakerIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {interest.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400">
                    {interest.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact Section with modern split layout */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="container">
          <div className="flex flex-col lg:flex-row bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
            {/* Contact Information */}
            <div className="lg:w-1/2 p-12 bg-gradient-to-br from-primary-500 to-primary-700 text-white">
              <h2 className="text-3xl font-bold mb-6">Get In Touch</h2>
              <p className="text-primary-100 mb-8 text-lg">
                I'm always open to discussing new projects, creative ideas or
                opportunities to be part of your vision.
              </p>

              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="p-3 bg-primary-400/30 rounded-full mr-4">
                    <EnvelopeIcon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">Email</h3>
                    <p className="text-primary-100">{profile.email}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="p-3 bg-primary-400/30 rounded-full mr-4">
                    <PhoneIcon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">Phone</h3>
                    <p className="text-primary-100">
                      {profile.phone || "Contact me via email"}
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="p-3 bg-primary-400/30 rounded-full mr-4">
                    <MapPinIcon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">Location</h3>
                    <p className="text-primary-100">
                      {profile.location || "Remote"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-4 mt-10">
                {profile.socialLinks?.linkedin && (
                  <a
                    href={profile.socialLinks.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white/20 p-3 rounded-full hover:bg-white/30 transition-colors"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                )}
                {profile.socialLinks?.github && (
                  <a
                    href={profile.socialLinks.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white/20 p-3 rounded-full hover:bg-white/30 transition-colors"
                  >
                    <Github className="w-5 h-5" />
                  </a>
                )}
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:w-1/2 p-12">
              <h3 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white">
                Send me a message
              </h3>
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* Project Detail Modal */}
      {selectedProject && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setSelectedProject(null)}
            />

            <div
              className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-2xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full sm:p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className="bg-white dark:bg-gray-800 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  onClick={() => setSelectedProject(null)}
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="w-full">
                {/* Project Header */}
                <div className="flex items-center space-x-6 mb-6">
                  <div className="w-20 h-20 rounded-xl overflow-hidden">
                    <img
                      src={selectedProject.image}
                      alt={selectedProject.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <h3 className="text-3xl font-bold text-gray-900 dark:text-white">
                        {selectedProject.title}
                      </h3>
                      {selectedProject.featured && <StarSolidIcon className="h-8 w-8 text-yellow-400" />}
                    </div>
                    
                    {/* Project Type and Company Info */}
                    <div className="mb-2">
                      <div className="flex items-center space-x-3">
                        <span className="text-lg text-gray-600 dark:text-gray-400">
                          {selectedProject.categories && selectedProject.categories.length > 0 
                            ? selectedProject.categories.join(' • ') 
                            : selectedProject.category || 'Project'
                          }
                        </span>
                        
                        {/* Project Type Badge */}
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          selectedProject.projectType === 'company' 
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                            : selectedProject.projectType === 'freelance' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : selectedProject.projectType === 'open-source'
                            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                        }`}>
                          {selectedProject.projectType === 'company' && '🏢 Company Project'}
                          {selectedProject.projectType === 'freelance' && '💼 Freelance Project'}
                          {selectedProject.projectType === 'open-source' && '🌐 Open Source'}
                          {selectedProject.projectType === 'personal' && '👤 Personal Project'}
                          {!selectedProject.projectType && '👤 Personal Project'}
                        </span>
                      </div>
                      
                      {/* Company Information */}
                      {selectedProject.company && (
                        <div className="mt-2 flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                            {selectedProject.projectType === 'freelance' ? 'Client:' : 'Company:'}
                          </span>
                          {selectedProject.companyWebsite ? (
                            <a 
                              href={selectedProject.companyWebsite} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-sm font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200 transition-colors flex items-center space-x-1"
                            >
                              <span>{selectedProject.company}</span>
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </a>
                          ) : (
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">
                              {selectedProject.company}
                            </span>
                          )}
                          <div className="w-2 h-2 bg-green-500 rounded-full" title="Verified professional work"></div>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-6 text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center space-x-1">
                        <CalendarIcon className="h-5 w-5" />
                        <span>
                          {selectedProject.createdAt 
                            ? new Date(selectedProject.createdAt.toDate()).getFullYear()
                            : 'Recent'
                          }
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <CodeBracketIcon className="h-5 w-5" />
                        <span>{selectedProject.technologies.length} Technologies</span>
                      </div>
                      {selectedProject.gallery && selectedProject.gallery.length > 0 && (
                        <div className="flex items-center space-x-1">
                          <PhotoIcon className="h-5 w-5" />
                          <span>{selectedProject.gallery.length} Media</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="flex space-x-4 mb-6">
                  {selectedProject.link && (
                    <a
                      href={selectedProject.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                    >
                      <EyeIcon className="h-5 w-5 mr-2" />
                      View Live Demo
                    </a>
                  )}
                  {selectedProject.github && (
                    <a
                      href={selectedProject.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-6 py-3 border border-gray-300 dark:border-gray-600 text-base font-medium rounded-xl text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                    >
                      <CodeBracketIcon className="h-5 w-5 mr-2" />
                      Source Code
                    </a>
                  )}
                </div>

                {/* Professional Context - For Company/Freelance Projects */}
                {(selectedProject.projectType === 'company' || selectedProject.projectType === 'freelance') && selectedProject.company && (
                  <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h4 className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                        Professional Work Experience
                      </h4>
                    </div>
                                         <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                       <div>
                         <span className="font-medium text-blue-800 dark:text-blue-200">Work Type:</span>
                         <span className="ml-2 text-blue-700 dark:text-blue-300">
                           {selectedProject.projectType === 'company' 
                             ? `${selectedProject.employmentType ? 
                                 selectedProject.employmentType.charAt(0).toUpperCase() + 
                                 selectedProject.employmentType.slice(1).replace('-', '-') 
                                 : 'Full-time'} Employment`
                             : 'Freelance Contract'}
                         </span>
                       </div>
                      <div>
                        <span className="font-medium text-blue-800 dark:text-blue-200">
                          {selectedProject.projectType === 'freelance' ? 'Client:' : 'Employer:'}
                        </span>
                        <span className="ml-2 text-blue-700 dark:text-blue-300">{selectedProject.company}</span>
                      </div>
                      {!selectedProject.github && !selectedProject.link && (
                        <div className="md:col-span-2">
                          <span className="font-medium text-blue-800 dark:text-blue-200">Note:</span>
                          <span className="ml-2 text-blue-700 dark:text-blue-300">
                            Source code and live demo may not be available due to confidentiality agreements and proprietary nature of the work.
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Description */}
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Project Description
                  </h4>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    {selectedProject.longDescription || selectedProject.description}
                  </p>
                </div>

                {/* Grid Layout for Features, Technologies, etc. */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Key Features */}
                  {selectedProject.features && selectedProject.features.length > 0 && (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                        ✨ Key Features
                      </h4>
                      <div className="space-y-4">
                        {selectedProject.features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="border-l-4 border-primary-500 pl-4">
                            <h5 className="font-semibold text-primary-600 dark:text-primary-400 mb-2">
                              {feature.name}
                            </h5>
                            <ul className="space-y-1">
                              {feature.subFeatures.map((subFeature, subIndex) => (
                                <li key={subIndex} className="flex items-start space-x-2">
                                  <CheckBadgeIcon className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                                  <span className="text-gray-600 dark:text-gray-300 text-sm">{subFeature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Technologies Used */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      🚀 Technologies Used
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      {selectedProject.technologies.map((tech, index) => (
                        <div
                          key={index}
                          className="flex items-center space-x-2 p-2 rounded-lg bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300"
                        >
                          <CodeBracketIcon className="h-4 w-4" />
                          <span className="font-medium text-sm">{tech}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Categories */}
                {selectedProject.categories && selectedProject.categories.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      📁 Categories
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedProject.categories.map((category, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium"
                        >
                          {category}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tags */}
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    🏷️ Tags
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-700 text-blue-700 dark:text-blue-300 text-sm font-medium"
                      >
                        <TagIcon className="h-3 w-3 mr-1" />
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Screenshots Gallery */}
                {selectedProject.gallery && selectedProject.gallery.length > 0 && (
                  <div className="mt-8">
                    <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-6 text-center">
                      🎥 Project Gallery ({selectedProject.gallery.length})
                    </h4>
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-2xl p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {selectedProject.gallery.map((item, index) => {
                          const mediaType = getMediaType(item);
                          const isYouTube = mediaType === 'youtube';
                          const videoId = isYouTube ? getYouTubeVideoId(item) : null;
                          const isGif = mediaType === 'gif';
                          
                          return (
                            <div key={index} className="relative group">
                              {isYouTube && videoId ? (
                                // YouTube Video Display
                                <div className="relative mx-auto w-full max-w-sm">
                                  <div className="relative bg-black rounded-2xl overflow-hidden shadow-2xl" style={{ aspectRatio: '16/9' }}>
                                    <iframe
                                      src={getYouTubeEmbedUrl(videoId)}
                                      title={`${selectedProject.title} video ${index + 1}`}
                                      className="w-full h-full"
                                      frameBorder="0"
                                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                      allowFullScreen
                                    ></iframe>
                                  </div>
                                  
                                  {/* Video title/number */}
                                  <div className="text-center mt-3">
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200">
                                      📺 Video Demo {index + 1}
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                // Mobile Frame for Images/GIFs
                                <div className="relative mx-auto w-64 h-auto bg-gray-900 dark:bg-gray-100 rounded-3xl p-3 shadow-2xl">
                                  {/* Phone notch/speaker */}
                                  <div className="absolute top-6 left-1/2 transform -translate-x-1/2 w-16 h-1 bg-gray-700 dark:bg-gray-300 rounded-full"></div>
                                  
                                  {/* Screen */}
                                  <div className="relative bg-black rounded-2xl overflow-hidden" style={{ aspectRatio: '9/16' }}>
                                    <img
                                      src={item}
                                      alt={`${selectedProject.title} screenshot ${index + 1}`}
                                      className="w-full h-full object-cover cursor-pointer transition-transform group-hover:scale-105"
                                      onError={(e) => {
                                        console.error(`Failed to load screenshot ${index + 1}:`, item);
                                        e.currentTarget.style.display = 'none';
                                      }}
                                      onLoad={() => {
                                        console.log(`Successfully loaded screenshot ${index + 1}:`, item);
                                      }}
                                      onClick={() => {
                                        window.open(item, '_blank');
                                      }}
                                    />
                                    
                                    {/* Overlay on hover */}
                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-white text-center">
                                        <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg px-4 py-2">
                                          <p className="text-sm font-medium">Click to view full size</p>
                                          <p className="text-xs opacity-80">Screenshot {index + 1}</p>
                                          {isGif && (
                                            <p className="text-xs opacity-80">🎬 Animated</p>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* Home indicator */}
                                  <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-gray-600 dark:bg-gray-400 rounded-full"></div>
                                </div>
                              )}
                              
                              {/* Screenshot title/number for images/GIFs */}
                              {!isYouTube && (
                                <div className="text-center mt-3">
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200">
                                    {isGif ? '🎬 ' : ''}Screenshot {index + 1}
                                  </span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* View all screenshots button if many */}
                      {selectedProject.gallery.length > 6 && (
                        <div className="text-center mt-6">
                          <button
                            onClick={() => {
                              selectedProject.gallery?.forEach((screenshot, index) => {
                                setTimeout(() => window.open(screenshot, '_blank'), index * 200);
                              });
                            }}
                            className="inline-flex items-center px-6 py-3 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                          >
                            🎥 View All Media
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
